/**
 * Utilitários de Data para o Frontend
 * Todas as funções retornam datas no timezone de Brasília (America/Sao_Paulo)
 */

const TIMEZONE = 'America/Sao_Paulo';

/**
 * Obtém a data atual em Brasília no formato YYYY-MM-DD
 * IMPORTANTE: Não usar new Date().toISOString() pois retorna UTC!
 */
export const getTodayBrasilia = (): string => {
  const now = new Date();
  // Converter para string no timezone de Brasília
  const options: Intl.DateTimeFormatOptions = {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  const parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(now);
  const year = parts.find(p => p.type === 'year')?.value || '';
  const month = parts.find(p => p.type === 'month')?.value || '';
  const day = parts.find(p => p.type === 'day')?.value || '';
  return `${year}-${month}-${day}`;
};

/**
 * Obtém o mês e ano atuais em Brasília
 */
export const getCurrentMonthYearBrasilia = (): { month: number; year: number } => {
  const today = getTodayBrasilia();
  const [year, month] = today.split('-').map(Number);
  return { month, year };
};

/**
 * Formata uma data para exibição no formato brasileiro DD/MM/YYYY
 * Lida corretamente com strings YYYY-MM-DD (sem converter para UTC)
 */
export const formatDateBR = (dateString: string): string => {
  if (!dateString) return '-';
  
  // Se for formato YYYY-MM-DD, formatar diretamente sem criar Date
  // Isso evita o problema de timezone onde new Date('2025-01-11') vira dia 10 em Brasília
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }
  
  // Para outros formatos (ISO completo), usar toLocaleDateString com timezone
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    timeZone: TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Formata uma data ISO para exibição no formato brasileiro com hora
 */
export const formatDateTimeBR = (dateString: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('pt-BR', {
    timeZone: TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Compara se uma data é hoje em Brasília
 */
export const isToday = (dateString: string): boolean => {
  const today = getTodayBrasilia();
  
  // Se for formato YYYY-MM-DD, comparar diretamente
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return today === dateString;
  }
  
  // Para outros formatos, converter com timezone
  const date = new Date(dateString);
  const dateFormatted = date.toLocaleDateString('en-CA', { timeZone: TIMEZONE });
  return today === dateFormatted;
};

/**
 * Compara se uma data é amanhã em Brasília
 */
export const isTomorrow = (dateString: string): boolean => {
  const tomorrowStr = getTomorrowBrasilia();
  
  // Se for formato YYYY-MM-DD, comparar diretamente
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return tomorrowStr === dateString;
  }
  
  // Para outros formatos, converter com timezone
  const date = new Date(dateString);
  const dateFormatted = date.toLocaleDateString('en-CA', { timeZone: TIMEZONE });
  return tomorrowStr === dateFormatted;
};

/**
 * Obtém a data de amanhã em Brasília no formato YYYY-MM-DD
 */
export const getTomorrowBrasilia = (): string => {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toLocaleDateString('en-CA', { timeZone: TIMEZONE });
};

/**
 * Extrai a parte da data (YYYY-MM-DD) de uma string ISO ou Date do backend
 * Converte para o timezone de Brasília antes de extrair
 */
export const extractDateYYYYMMDD = (dateInput: string | Date): string => {
  if (!dateInput) return '';
  
  // Se já for string YYYY-MM-DD, retornar diretamente
  if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    return dateInput;
  }
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  return date.toLocaleDateString('en-CA', { timeZone: TIMEZONE });
};

/**
 * Formata uma data para exibição extensa no formato brasileiro
 * Exemplo: "25 de dezembro de 2025"
 */
export const formatDateLongBR = (dateInput: string | Date): string => {
  if (!dateInput) return '-';
  
  // Se for string YYYY-MM-DD, criar Date com horário meio-dia para evitar problema de timezone
  if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    const [year, month, day] = dateInput.split('-').map(Number);
    // Usar horário meio-dia para evitar que UTC converta para dia anterior
    const date = new Date(year, month - 1, day, 12, 0, 0);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  return date.toLocaleDateString('pt-BR', {
    timeZone: TIMEZONE,
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

